import styled from '@emotion/styled'
import L1Select from '../l1/L1Select'
import { L2TabularListActions, L2TabularListContent, L2TabularListHeader, L2TabularListRoot } from '../l2/L2TabularList'
import { L2TabularListControl, L2TabularListControlLabel, L2TabularListItemEdit, L2TabularListItemSummary } from '../l2/L2TabularListItem'
import { L4NoPrint } from '../l4/L4Print'
import { L1ReasonableButton } from '../l1/L1Button'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { getIcdCodeForUlcer, getSitesForUlcerType, getStageForSiteAndUlcerType, getUlcerTypes, newUlcerReviewItem, UlcerItem, UlcerReviewItem, ulcerReviewItemIsValid } from '@fusion-advantage/formlib'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import L2ControlGrid from '../l2/L2ControlGrid'
import L2ControlGridLayout from '../l2/L2ControlGridLayout'
import _ from 'lodash'

interface L3UlcerReviewProps {
	mappings: UlcerItem[],
	models: UlcerReviewItem[],
	onChange: (models: UlcerReviewItem[]) => void,
}

const L3UlcerReviewContainer = styled.div`
	padding: 0;
	box-sizing: border-box;
`

const UlcerReviewRowContainer = styled.div(({theme}) => ({
	padding: '1em',
	width: '100%',
}))

type UlcerReviewRowProps = {
	mappings: UlcerItem[],
	initialValue?: UlcerReviewItem,
	onChange: (model: UlcerReviewItem) => void,
}

const UlcerReviewRow = ({initialValue, mappings, onChange}: UlcerReviewRowProps) => {
	const [selectedUlcerType, setSelectedUlcerType] = useState(initialValue?.ulcerType || "")
	const [selectedUlcerSite, setSelectedUlcerSite] = useState(initialValue?.sites || "")
	const [selectedUlcerStage, setSelectedUlcerStage] = useState(initialValue?.stage || "")

	const handleSelectUlcerType = (e: ChangeEvent<HTMLSelectElement>) => {
		setSelectedUlcerType(e.target.value)
		setSelectedUlcerSite('')
		setSelectedUlcerStage('')
	}

	const handleSelectUlcerSite = (e: ChangeEvent<HTMLSelectElement>) => {
		setSelectedUlcerSite(e.target.value)
		setSelectedUlcerStage('')
	}

	const handleSelectUlcerStage = (e: ChangeEvent<HTMLSelectElement>) => {
		setSelectedUlcerStage(e.target.value)
	}

	useEffect(() => {
		handleChange()
	}, [selectedUlcerType, selectedUlcerSite, selectedUlcerStage])


	const handleChange = useCallback(() => {
		const icdCode = getIcdCodeForUlcer(selectedUlcerType, selectedUlcerSite, selectedUlcerStage, mappings)
		const model: UlcerReviewItem = newUlcerReviewItem(selectedUlcerType, selectedUlcerSite, selectedUlcerStage, icdCode)
		onChange(model)
	}, [selectedUlcerType, selectedUlcerSite, selectedUlcerStage])

	const ulcerTypes = useMemo(() => getUlcerTypes(mappings), [mappings])
	const ulcerSites = useMemo(() => getSitesForUlcerType(selectedUlcerType, mappings), [selectedUlcerType, mappings])
	const ulcerStages = useMemo(() => getStageForSiteAndUlcerType(selectedUlcerType, selectedUlcerSite, mappings), [selectedUlcerType, selectedUlcerSite, mappings])

	return (
		<>
			<UlcerReviewRowContainer>
				<L2ControlGrid columnCount={3} layout={L2ControlGridLayout.EqualDividedColumn}>
					<L2TabularListControl>
						<L2TabularListControlLabel>Ulcer type</L2TabularListControlLabel>
						<L1Select onChange={handleSelectUlcerType} value={selectedUlcerType}>
						{
							ulcerTypes.map((ulcerType, index) => (
								<option key={index} value={ulcerType}>{ulcerType}</option>
							))
						}
						</L1Select>
					</L2TabularListControl>

					<L2TabularListControl>
						<L2TabularListControlLabel>Ulcer site</L2TabularListControlLabel>
						<L1Select onChange={handleSelectUlcerSite} value={selectedUlcerSite} disabled={Boolean(selectedUlcerType) === false}>
						{
							ulcerSites.map((ulcerSite, index) => (
								<option key={index} value={ulcerSite}>{ulcerSite}</option>
							))
						}
						</L1Select>
					</L2TabularListControl>

					<L2TabularListControl>
						<L2TabularListControlLabel>Ulcer stage</L2TabularListControlLabel>
						<L1Select onChange={handleSelectUlcerStage} value={selectedUlcerStage} disabled={Boolean(selectedUlcerSite) === false}>
						{
							ulcerStages.map((ulcerStage, index) => (
								<option key={index} value={ulcerStage}>{ulcerStage}</option>
							))
						}
						</L1Select>
					</L2TabularListControl>
				</L2ControlGrid>
			</UlcerReviewRowContainer>
		</>
	)
}

const L3UlcerReview = ({mappings, models, onChange}: L3UlcerReviewProps) => {
	//const [modelState, setModelState] = useState<UlcerReviewItem[]>(models)
	const [editIndex, setEditIndex] = useState<number|null>(null)

	const handleToggleEditRow = (idx: number) => {
		if(editIndex === idx) {
			return setEditIndex(null)
		}

		setEditIndex(idx)
	}

	const handleRemoveModel = (idx: number) => {
		if(confirm('Delete this item?')) {
			const modelsTemp = _.cloneDeep(models)
			modelsTemp.splice(idx, 1)
	
			if(editIndex === idx) {
				handleToggleEditRow(idx)
			}
	
			onChange(modelsTemp)
		}
	}

	const handleUpdateModel = (model: UlcerReviewItem, idx: number) => {
		const modelsTemp = _.cloneDeep(models)
		modelsTemp.splice(idx, 1, model)

		onChange(modelsTemp)
	}

	const handleAddModel = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()

		onChange([...models, newUlcerReviewItem()])

		setEditIndex(models.length)
	}
	
	return (
		<L3UlcerReviewContainer>
			<L2TabularListRoot columnCount={3}>
				<L2TabularListHeader>
					<div>Ulcer type</div>
					<div>Site</div>
					<div>Stage / Severity</div>
				</L2TabularListHeader>
				<L2TabularListContent>
				{
					models.map((model, idx) => (
						editIndex === idx ? (
							<L2TabularListItemEdit
								key={idx}
								onCloseClick={() => handleToggleEditRow(idx)}
								onDeleteClick={() => handleRemoveModel(idx)}
							>
								<UlcerReviewRow initialValue={model} mappings={mappings} onChange={(m) => handleUpdateModel(m, idx)}  />
							</L2TabularListItemEdit>
						) : (
							<L2TabularListItemSummary
								key={idx}
								onClick={() => handleToggleEditRow(idx)}
							>
								<div>{model.ulcerType}</div>
								<div>{model.sites}</div>
								<div>{model.stage}</div>
							</L2TabularListItemSummary>
						)
						
					))
				}
				</L2TabularListContent>
				<L2TabularListActions>
					<L4NoPrint>
						<L1ReasonableButton icon={faPlus} label="Add" onClick={handleAddModel} />
					</L4NoPrint>
				</L2TabularListActions>
			</L2TabularListRoot>
		</L3UlcerReviewContainer>
	)
}

export default L3UlcerReview